import type {
  AnalyticsResponse,
  Enterprise,
  EnterpriseStatus,
  NotificationSettingsResponse,
  NotificationTemplate,
  Page,
  ProductionCategory,
  TicketType,
} from '@promtver/contracts';
// UserRole используется как значение (enum), поэтому импортируется не только как тип.
import { B2BRequestKind, B2BRequestStatus, UserRole } from '@promtver/contracts';

import { apiRequest } from './client';
import type {
  AdminAnalyticsQuery,
  AdminB2BRequest,
  AdminUser,
  AuditLogEntry,
  DictionaryItem,
  EnterprisePage,
  ExportRecord,
  ExportJournalPage,
  ModerationEnterprise,
  PassportTemplate,
  PromoBlock,
} from './types';

/**
 * Обращения к backend API для роли region_admin / platform_operator
 * (ТЗ §7.1, Приложение В). Права на объекты проверяются на сервере.
 */

/* ────────────────────────────  АНАЛИТИКА  ─────────────────────────── */

export async function getAnalytics(
  query: AdminAnalyticsQuery,
): Promise<AnalyticsResponse> {
  return apiRequest<AnalyticsResponse>('/admin/analytics', {
    query: {
      enterpriseId: query.enterpriseId,
      districtId: query.districtId,
      category: query.category,
      from: query.from,
      to: query.to,
      channel: query.channel,
      ageBucket: query.ageBucket,
      ticketType: query.ticketType,
      repeat: query.repeat,
    },
  });
}

/* ───────────────────────────  МОДЕРАЦИЯ  ───────────────────────────── */

export async function getEnterprisesForModeration(
  status?: EnterpriseStatus,
  cursor?: string,
): Promise<EnterprisePage> {
  const body = await apiRequest<{ items?: Enterprise[]; nextCursor?: string | null }>(
    '/admin/enterprises',
    { query: { status, cursor, limit: 50 } },
  );
  return { items: body.items ?? [], nextCursor: body.nextCursor ?? null };
}

export async function getEnterpriseForModeration(id: string): Promise<ModerationEnterprise> {
  const body = await apiRequest<{ enterprise: ModerationEnterprise } | ModerationEnterprise>(
    `/admin/enterprises/${encodeURIComponent(id)}`,
  );
  if (body && 'enterprise' in body) return body.enterprise;
  return body as ModerationEnterprise;
}

/** Публикация карточки объекта (FR-ADM-02). */
export async function publishEnterprise(id: string): Promise<Enterprise> {
  const body = await apiRequest<{ enterprise: Enterprise } | Enterprise>(
    `/admin/enterprises/${encodeURIComponent(id)}/publish`,
    { method: 'POST', body: { action: 'publish' } },
  );
  if (body && 'enterprise' in body) return body.enterprise;
  return body as Enterprise;
}

/** Снятие карточки с публикации (FR-ADM-02). */
export async function unpublishEnterprise(id: string): Promise<Enterprise> {
  const body = await apiRequest<{ enterprise: Enterprise } | Enterprise>(
    `/admin/enterprises/${encodeURIComponent(id)}/publish`,
    { method: 'POST', body: { action: 'unpublish' } },
  );
  if (body && 'enterprise' in body) return body.enterprise;
  return body as Enterprise;
}

/** Отклонение карточки — причина обязательна и фиксируется в журнале. */
export async function rejectEnterprise(id: string, reason: string): Promise<Enterprise> {
  const body = await apiRequest<{ enterprise: Enterprise } | Enterprise>(
    `/admin/enterprises/${encodeURIComponent(id)}/reject`,
    { method: 'POST', body: { reason } },
  );
  if (body && 'enterprise' in body) return body.enterprise;
  return body as Enterprise;
}

/* ───────────────────────────  СПРАВОЧНИКИ  ─────────────────────────── */

type DictionaryKind = 'districts' | 'categories' | 'ticket-types' | 'safety-rules';

export async function getDictionary(kind: DictionaryKind): Promise<DictionaryItem[]> {
  const body = await apiRequest<{ items?: DictionaryItem[] } | DictionaryItem[]>(
    `/admin/dictionaries/${kind}`,
  );
  if (Array.isArray(body)) return body;
  return body.items ?? [];
}

export async function createDictionaryItem(
  kind: DictionaryKind,
  item: Pick<DictionaryItem, 'code' | 'name' | 'description'> & { isActive?: boolean },
): Promise<DictionaryItem> {
  const body = await apiRequest<{ item: DictionaryItem } | DictionaryItem>(
    `/admin/dictionaries/${kind}`,
    { method: 'POST', body: item },
  );
  if (body && 'item' in body) return body.item;
  return body as DictionaryItem;
}

export async function updateDictionaryItem(
  kind: DictionaryKind,
  id: string,
  patch: Partial<DictionaryItem>,
): Promise<DictionaryItem> {
  const body = await apiRequest<{ item: DictionaryItem } | DictionaryItem>(
    `/admin/dictionaries/${kind}/${encodeURIComponent(id)}`,
    { method: 'PATCH', body: patch },
  );
  if (body && 'item' in body) return body.item;
  return body as DictionaryItem;
}

export async function deleteDictionaryItem(kind: DictionaryKind, id: string): Promise<void> {
  await apiRequest<void>(`/admin/dictionaries/${kind}/${encodeURIComponent(id)}`, {
    method: 'DELETE',
  });
}

export async function getPromoBlocks(): Promise<PromoBlock[]> {
  const body = await apiRequest<{ items?: PromoBlock[] } | PromoBlock[]>('/admin/promo-blocks');
  if (Array.isArray(body)) return body;
  return body.items ?? [];
}

export async function createPromoBlock(block: Omit<PromoBlock, 'id'>): Promise<PromoBlock> {
  const body = await apiRequest<{ item: PromoBlock } | PromoBlock>('/admin/promo-blocks', {
    method: 'POST',
    body: block,
  });
  if (body && 'item' in body) return body.item;
  return body as PromoBlock;
}

export async function updatePromoBlock(id: string, patch: Partial<PromoBlock>): Promise<PromoBlock> {
  const body = await apiRequest<{ item: PromoBlock } | PromoBlock>(
    `/admin/promo-blocks/${encodeURIComponent(id)}`,
    { method: 'PATCH', body: patch },
  );
  if (body && 'item' in body) return body.item;
  return body as PromoBlock;
}

export async function deletePromoBlock(id: string): Promise<void> {
  await apiRequest<void>(`/admin/promo-blocks/${encodeURIComponent(id)}`, { method: 'DELETE' });
}

/* ────────────────────────  ЗАЯВКИ ПРЕДПРИЯТИЙ  ─────────────────────── */

/** Вид заявки из строки ответа — значения сверяем с контрактом, иначе берём заявку на подключение. */
function toRequestKind(value: string | null): B2BRequestKind {
  return Object.values(B2BRequestKind).find((kind) => kind === value) ?? B2BRequestKind.Onboarding;
}

/** Статус заявки из строки ответа. */
function toRequestStatus(value: string | null): B2BRequestStatus {
  return Object.values(B2BRequestStatus).find((status) => status === value) ?? B2BRequestStatus.New;
}

/**
 * Приведение строки журнала заявок к контракту панели.
 * Поля userId/email/legalName/inn приходят с заявкой на регистрацию предприятия:
 * без идентификатора учётной записи нельзя назначить представителя модератором.
 */
function normalizeB2BRequest(raw: unknown): AdminB2BRequest {
  const item = (raw ?? {}) as Record<string, unknown>;
  const readText = (key: string): string | null => {
    const value = item[key];
    return typeof value === 'string' && value.trim() ? value : null;
  };

  return {
    id: readText('id') ?? '',
    enterpriseId: readText('enterpriseId'),
    kind: toRequestKind(readText('kind')),
    contactName: readText('contactName') ?? '',
    contactPhone: readText('contactPhone') ?? '',
    contactEmail: readText('contactEmail'),
    companyName: readText('companyName'),
    district: readText('district'),
    message: readText('message'),
    slotsPerWeek: readText('slotsPerWeek'),
    status: toRequestStatus(readText('status')),
    managerId: readText('managerId'),
    createdAt: readText('createdAt') ?? '',
    userId: readText('userId'),
    email: readText('email') ?? readText('contactEmail'),
    legalName: readText('legalName') ?? readText('companyName'),
    inn: readText('inn'),
  };
}

/**
 * Журнал заявок предприятий (FR-B2B-02).
 * Список заявок — общий для админ-панели; по умолчанию отдаём все, без курсора.
 */
export async function getB2BRequests(cursor?: string): Promise<Page<AdminB2BRequest>> {
  const body = await apiRequest<{ items?: unknown[]; nextCursor?: string | null }>('/b2b/requests', {
    query: { cursor, limit: 100 },
  });
  return {
    items: (body.items ?? []).map(normalizeB2BRequest),
    nextCursor: body.nextCursor ?? null,
  };
}

/* ──────────────────────────────  РОЛИ  ─────────────────────────────── */

export async function getAdminUsers(role?: UserRole): Promise<Page<AdminUser>> {
  const body = await apiRequest<{ items?: AdminUser[]; nextCursor?: string | null }>(
    '/admin/users',
    { query: { role, limit: 100 } },
  );
  return { items: body.items ?? [], nextCursor: body.nextCursor ?? null };
}

/**
 * Назначение роли и привязки к объекту (FR-ADM-04).
 * Контракт сервера: POST /admin/roles с телом { userId, role, enterpriseId }.
 * Сервер подтверждает изменение признаком ok и может вернуть обновлённую учётную запись.
 */
export async function assignRole(
  userId: string,
  role: UserRole,
  enterpriseId?: string | null,
): Promise<{ ok: boolean; user?: AdminUser }> {
  const body = await apiRequest<{ ok?: boolean; user?: AdminUser }>('/admin/roles', {
    method: 'POST',
    body: { userId, role, enterpriseId: enterpriseId ?? null },
  });
  return { ok: body.ok ?? true, user: body.user };
}

/** Назначение модератора объекта (FR-ADM-04). */
export async function assignObjectModerator(
  userId: string,
  enterpriseId: string,
): Promise<{ ok: boolean; user?: AdminUser }> {
  return assignRole(userId, UserRole.ObjectModerator, enterpriseId);
}

/** Снятие прав модератора (FR-ADM-04). */
export async function revokeObjectModerator(userId: string): Promise<{ ok: boolean; user?: AdminUser }> {
  return assignRole(userId, UserRole.Guest, null);
}

/** Блокировка или разблокировка учётной записи (FR-ADM-04). */
export async function setUserBlocked(userId: string, blocked: boolean): Promise<AdminUser> {
  const body = await apiRequest<{ user: AdminUser } | AdminUser>(
    `/admin/users/${encodeURIComponent(userId)}/block`,
    { method: 'POST', body: { blocked } },
  );
  if (body && 'user' in body) return body.user;
  return body as AdminUser;
}

/* ────────────────────────────  ВЫГРУЗКИ  ───────────────────────────── */

export interface ExportJournalFilters {
  cursor?: string;
  limit?: number;
}

export async function getExportJournal(filters: ExportJournalFilters = {}): Promise<ExportJournalPage> {
  const body = await apiRequest<{ items?: ExportRecord[]; nextCursor?: string | null }>(
    '/admin/reports/exports',
    { query: { cursor: filters.cursor, limit: filters.limit ?? 50 } },
  );
  return { items: body.items ?? [], nextCursor: body.nextCursor ?? null };
}

/**
 * Фиксация выгрузки в журнале с указанием пользователя и времени (FR-ADM-05).
 * Пользователь и время проставляет сервер.
 */
export async function logExport(record: {
  format: 'csv' | 'xlsx';
  reportType: string;
  from: string;
  to: string;
  rowCount: number;
}): Promise<ExportRecord> {
  const body = await apiRequest<{ export: ExportRecord } | ExportRecord>(
    '/admin/reports/exports',
    { method: 'POST', body: record },
  );
  if (body && 'export' in body) return body.export;
  return body as ExportRecord;
}

/** Серверная выгрузка по утверждённой форме (FR-OBS-04, FR-ADM-05). */
export async function requestServerExport(
  format: 'csv' | 'xlsx',
  from: string,
  to: string,
): Promise<{ url: string; format: 'csv' | 'xlsx'; exportedAt: string }> {
  return apiRequest<{ url: string; format: 'csv' | 'xlsx'; exportedAt: string }>(
    '/admin/reports/export',
    { query: { format, from, to } },
  );
}

/* ─────────────────────────  ГОСРЕЕСТР (ПАСПОРТ)  ──────────────────── */

export async function getPassportTemplates(): Promise<PassportTemplate[]> {
  const body = await apiRequest<{ items?: PassportTemplate[] } | PassportTemplate[]>(
    '/admin/registry/passport-templates',
  );
  if (Array.isArray(body)) return body;
  return body.items ?? [];
}

export async function savePassportTemplate(template: PassportTemplate): Promise<PassportTemplate> {
  const body = await apiRequest<{ template: PassportTemplate } | PassportTemplate>(
    `/admin/registry/passport-templates/${encodeURIComponent(template.id)}`,
    { method: 'PUT', body: template },
  );
  if (body && 'template' in body) return body.template;
  return body as PassportTemplate;
}

/* ────────────────────────────  УВЕДОМЛЕНИЯ  ────────────────────────── */

export async function getNotificationTemplates(): Promise<NotificationTemplate[]> {
  const body = await apiRequest<NotificationSettingsResponse>('/admin/notification-templates');
  return body.templates ?? [];
}

export async function saveNotificationTemplates(
  templates: NotificationTemplate[],
): Promise<NotificationTemplate[]> {
  const body = await apiRequest<NotificationSettingsResponse>('/admin/notification-templates', {
    method: 'PUT',
    body: { templates },
  });
  return body.templates ?? templates;
}

/* ─────────────────────────  ЖУРНАЛ АУДИТА  ────────────────────────── */

export async function getAuditLog(cursor?: string): Promise<Page<AuditLogEntry>> {
  const body = await apiRequest<{ items?: AuditLogEntry[]; nextCursor?: string | null }>(
    '/admin/audit-log',
    { query: { cursor, limit: 50 } },
  );
  return { items: body.items ?? [], nextCursor: body.nextCursor ?? null };
}

/** Справочные значения для фильтров аналитики. */
export async function getAnalyticsDirectories(): Promise<{
  districts: Array<{ id: string; name: string }>;
  enterprises: Array<{ id: string; name: string }>;
  channels: string[];
  ageBuckets: string[];
  ticketTypes: TicketType[];
  categories: ProductionCategory[];
}> {
  return apiRequest<{
    districts: Array<{ id: string; name: string }>;
    enterprises: Array<{ id: string; name: string }>;
    channels: string[];
    ageBuckets: string[];
    ticketTypes: TicketType[];
    categories: ProductionCategory[];
  }>('/admin/analytics/directories');
}
